/**
 * La solapa «Ventas por consorcio» de la ficha del administrador.
 *
 * Es el pedido del dueño, textual: «cuando abrís el administrador te aparezcan
 * las ventas que le hiciste a ese administrador correspondientes a cada
 * consorcio». Por eso es una solapa dibujada en línea y no sólo un botón que
 * abre un modal: la palabra fue «te aparezcan», no «podés pedir».
 *
 * Qué dibuja y qué NO calcula
 * ---------------------------
 * Llama a la acción de sólo lectura `property.administrador.ventas_por_consorcio`
 * y dibuja el cuadro que devuelve con `TabularReport`, que es el mismo renderer
 * de los trece reportes del ERP. **Acá no se agrupa, no se suma y no se ordena
 * nada**: el agrupado por consorcio, los subtotales y el total los calcula el
 * servidor. Una segunda suma del lado del cliente daría dos respuestas a la
 * misma pregunta, que es exactamente lo que este cuadro existe para evitar.
 *
 * Por qué una solapa con un panel y no una grilla de campos
 * ----------------------------------------------------------
 * Porque de la persona a sus ventas hay DOS saltos
 * (`administrador.partner.subscriptions`) y una grilla (`One2ManyField`) arma su
 * consulta con `meta.relation_field`, o sea que exige una relación directa. Y
 * aunque la hubiera, una grilla no agrupa — y agrupado por consorcio es
 * justamente lo que se pidió. El backend declara la solapa sin campos y este
 * archivo se engancha por su ETIQUETA en `tab-panels.tsx`.
 *
 * El caso sin permiso, dicho y no escondido
 * ------------------------------------------
 * Un rol que no puede leer el reporte ve el motivo, no un panel vacío. Es la
 * misma decisión —y el mismo texto— que `VentaRapidaPanel`: desaparecer sin
 * dejar rastro hace que quien lo vio en la máquina de al lado crea que el
 * sistema está roto.
 */

import { useQuery } from '@tanstack/react-query';

import { IconAlert } from '@/components/shell/icons';
import { ApiError, api } from '@/lib/api';
import type { ActionResult, TabularReportData } from '@/lib/types';
import { AvisoDeBloqueo } from '@/views/MotivoBloqueo';
import { TabularReport } from '@/views/TabularReport';

const MODELO = 'property.administrador';
const ACCION = 'ventas_por_consorcio';

/** El cuadro que trajo la acción, o `null` si la respuesta no tiene forma de cuadro. */
function cuadroDe(resultado: ActionResult | undefined): TabularReportData | null {
  const resultados = (resultado as { results?: unknown } | undefined)?.results;
  if (!Array.isArray(resultados) || resultados.length === 0) return null;
  const datos = (resultados[0] as { data?: unknown }).data;
  if (datos === null || typeof datos !== 'object') return null;
  const cuadro = (datos as { table?: unknown }).table;
  if (cuadro === null || typeof cuadro !== 'object') return null;
  const t = cuadro as Record<string, unknown>;
  if (typeof t.title !== 'string' || !Array.isArray(t.columns) || !Array.isArray(t.sections)) {
    return null;
  }
  return cuadro as TabularReportData;
}

export interface VentasPorConsorcioPanelProps {
  /** El administrador cuya ficha se está mirando. `null` (alta sin guardar) no dibuja. */
  recordId: number | null;
}

export function VentasPorConsorcioPanel({ recordId }: VentasPorConsorcioPanelProps) {
  const cuadro = useQuery({
    queryKey: ['ventas-por-consorcio', recordId] as const,
    enabled: recordId !== null,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const respuesta = await api.callAction(MODELO, recordId as number, ACCION, {});
      return cuadroDe(respuesta);
    },
  });

  if (recordId === null) {
    return (
      <AvisoDeBloqueo motivo="Guardá el administrador y acá vas a ver, consorcio por consorcio, todo lo que se le vendió." />
    );
  }

  if (
    cuadro.isError &&
    cuadro.error instanceof ApiError &&
    (cuadro.error.isPermissionError || cuadro.error.status === 404)
  ) {
    return (
      <AvisoDeBloqueo motivo="Tu usuario no tiene permiso para ver lo que se le vendió a este administrador. Si lo necesitás, pedíselo a quien administra los accesos." />
    );
  }

  if (cuadro.isError) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-line p-3">
        <IconAlert className="h-5 w-5 shrink-0 text-danger" />
        <p className="text-sm text-fg-muted">
          No se pudo traer lo que se le vendió.{' '}
          {cuadro.error instanceof ApiError ? cuadro.error.message : 'Reintentá en un momento.'}
        </p>
      </div>
    );
  }

  if (cuadro.isPending) {
    return (
      <p className="rounded-lg border border-line px-3 py-4 text-sm text-fg-muted">
        Buscando lo que se le vendió, consorcio por consorcio…
      </p>
    );
  }

  if (cuadro.data === null) {
    return (
      <p className="rounded-lg border border-line px-3 py-4 text-sm text-fg-muted">
        No se pudo leer la respuesta del servidor.
      </p>
    );
  }

  return (
    <section aria-labelledby="ventas-por-consorcio-titulo" className="mb-2">
      <h3
        id="ventas-por-consorcio-titulo"
        className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-faint"
      >
        Lo que le vendimos, por consorcio
      </h3>
      {/* `sinEncabezado`: el título del cuadro lo pone el `h3` de arriba, que es
          el que corresponde a una solapa. Repetirlo dejaría dos títulos a diez
          píxeles de distancia. */}
      <TabularReport datos={cuadro.data} sinEncabezado />
      <p className="mt-3 text-xs text-fg-faint">
        Le compra el administrador y cada trámite es <strong>para</strong> un consorcio: por eso el
        desglose es por edificio. Los trámites que son del administrador —matrícula, DDJJ— van
        aparte al final y <strong>también suman en el total</strong>, así el número cierra con el de
        su administración. Para cambiar el consorcio de una venta, abrila desde acá y usá «¿Para qué
        consorcio es?».
      </p>
    </section>
  );
}
